<template>
  <ValidationObserver v-slot="{ invalid }">
    <Loader cover v-if="courseFaqProcessing || courseOutlineProcessing"/>

    <vx-card v-else>

      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2">
          <h4 class="mb-2">{{ $t('vdo-set.course-outline-unit-qty') }}</h4>
          <v-select :clearable="false" :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    :options="courseOutlineUnits"
                    class="mb-4 md:mb-0"
                    label="nameLocal"
                    selectOnTab
                    v-model="selectCourseOutlineUnit">

            <!--label option-->
            <template #selected-option="{ seq, nameLocal }">
              {{ seq }}. {{ nameLocal }}
            </template>

            <!--option list-->
            <template v-slot:option="option">
              {{ option.seq }}. {{ option.nameLocal }}
            </template>

          </v-select>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full">
          <h4 class="mb-2">{{ $t('course-faq.question') }}</h4>
          <ValidationProvider :name="$t('course-faq.question')" rules="required" v-slot="{ errors }">
            <quill-editor :options="questionEditorOption" v-model="courseFaq.question">
            </quill-editor>
            <span class="text-danger text-sm" v-show="errors[0]">{{errors[0]}}</span>
          </ValidationProvider>
        </div>
      </div>

      <div class="vx-row mt-2">
        <div class="vx-col w-full">
          <h4 class="mb-2">{{ $t('course-faq.answer') }}</h4>
          <ValidationProvider :name="$t('course-faq.answer')" rules="required" v-slot="{ errors }">
            <quill-editor :options="answerEditorOption" v-model="courseFaq.answer">
            </quill-editor>
            <span class="text-danger text-sm" v-show="errors[0]">{{errors[0]}}</span>
          </ValidationProvider>
        </div>
      </div>

      <!----------------------------------------- Actions Button ---------------------------------------------------->
      <div class="vx-row mt-4">
        <div class="vx-col w-full">
          <vs-button :disabled="courseFaqProcessing || invalid"
                     @click.prevent="onSaveClick">
            {{ $t('general.save') }}
          </vs-button>

          <vs-button :disabled="courseFaqProcessing"
                     @click.prevent="onCancelClick"
                     class="ml-2" color="warning">
            {{ $t('general.cancel') }}
          </vs-button>
        </div>

      </div>
    </vx-card>
  </ValidationObserver>
</template>

<script>
  import store from '../../../../../store';
  import {createHelpers} from 'vuex-map-fields';
  import {createNamespacedHelpers} from "vuex";
  import CourseFaq from '../../../../../store/modules/class-room/course-faq/index';
  import CourseOutline from '../../../../../store/modules/courses/course-outline/index';
  import {confirmSave} from "../../../../../utils/dialogs";
  import {showStateNotification} from "../../../../../utils/notification";
  import Loader from '../../../../../components/common-ui/loader-block/index'
  import vSelect from "vue-select";
  import {quillEditor} from 'vue-quill-editor'
  import 'quill/dist/quill.core.css'
  import 'quill/dist/quill.snow.css'
  import 'quill/dist/quill.bubble.css'

  if (!store.state.CourseFaq) store.state.registerModule(`CourseFaq`, CourseFaq);
  if (!store.state.CourseOutline) store.state.registerModule(`CourseOutline`, CourseOutline);

  const {
    mapActions: mapCourseFaqActions,
  } = createNamespacedHelpers(`CourseFaq`);
  const {
    mapActions: mapCourseOutlineActions,
  } = createNamespacedHelpers(`CourseOutline`);

  const {
    mapFields: mapCourseFaqFields,
  } = createHelpers({
    getterType: `CourseFaq/getField`,
    mutationType: `CourseFaq/updateField`,
  });
  const {
    mapFields: mapCourseOutlineFields,
  } = createHelpers({
    getterType: `CourseOutline/getField`,
    mutationType: `CourseOutline/updateField`,
  });

  export default {
    name: "CourseFaqEditForm",
    data() {
      return {
        courseFaq: {
          question: '',
          answer: '',
        },
        courseOutlineUnits: [],
        selectCourseOutlineUnit: null,
        questionEditorOption: {
          modules: {
            toolbar: [
              ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
              ['blockquote', 'code-block'],

              [{'header': 1}, {'header': 2}],               // custom button values
              [{'list': 'ordered'}, {'list': 'bullet'}],
              [{'script': 'sub'}, {'script': 'super'}],      // superscript/subscript
              [{'indent': '-1'}, {'indent': '+1'}],          // outdent/indent
              [{'direction': 'rtl'}],                         // text direction

              [{'size': ['small', false, 'large', 'huge']}],  // custom dropdown
              [{'header': [1, 2, 3, 4, 5, 6, false]}],

              [{'color': []}, {'background': []}],          // dropdown with defaults from theme
              [{'font': []}],
              [{'align': []}],

              ['clean']                                         // remove formatting button
            ],
          }
        },
        answerEditorOption: {
          modules: {
            toolbar: [
              ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
              ['blockquote', 'code-block'],

              [{'header': 1}, {'header': 2}],               // custom button values
              [{'list': 'ordered'}, {'list': 'bullet'}],
              [{'script': 'sub'}, {'script': 'super'}],      // superscript/subscript
              [{'indent': '-1'}, {'indent': '+1'}],          // outdent/indent
              [{'direction': 'rtl'}],                         // text direction

              [{'size': ['small', false, 'large', 'huge']}],  // custom dropdown
              [{'header': [1, 2, 3, 4, 5, 6, false]}],

              [{'color': []}, {'background': []}],          // dropdown with defaults from theme
              [{'font': []}],
              [{'align': []}],

              ['clean'],                                         // remove formatting button

              ['link'],
            ],
          }
        },
      }
    },
    components: {
      Loader,
      vSelect,
      quillEditor,
    },
    computed: {
      ...mapCourseFaqFields({
        courseFaqProcessing: 'processing',
        courseFaqError: 'error',
        courseFaqSuccess: 'success'
      }),
      ...mapCourseOutlineFields({
        courseOutlineProcessing: 'processing',
      }),
    },
    watch: {
      courseFaqSuccess(data) {
        showStateNotification(this.$vs, data);
      },
      courseFaqError: async function (data) {
        showStateNotification(this.$vs, data);
      },
    },
    methods: {
      //---------------------------------------- State Function ------------------------------------------------------//
      ...mapCourseFaqActions({
        getCourseFaq: 'getCourseFaq',
        addCourseFaq: 'addCourseFaq',
        updateCourseFaq: 'updateCourseFaq',
      }),
      ...mapCourseOutlineActions({
        getCourseOutlineUnitsByCourseOutlineId: 'getCourseOutlineUnitsByCourseOutlineId',
      }),
      //---------------------------------------- Event Function ------------------------------------------------------//
      async onSaveClick() {
        let payload = {
          courseOutlineUnitId: this.selectCourseOutlineUnit.id,
          courseFAQInput: {
            question: this.courseFaq.question,
            answer: this.courseFaq.answer,
          }
        }
        if (this.courseFaq.id) {
          payload = {
            id: this.courseFaq.id,
            ...payload,
          };
        } else {
          payload = {
            classSectionId: this.$route.params.classSectionId,
            ...payload,
          };
        }
        await confirmSave(this.$vs, this.saveCourseFaq, payload);
      },
      onCancelClick() {
        this.$router.push({
          path: `/course-faq/${this.$route.params.classSectionId}/${this.$route.params.courseOutlineId}`,
          params: {
            courseOutlineUnitId: this.$route.params.courseOutlineUnitId,
            classSectionId: this.$route.params.classSectionId,
          }
        });
      },
      //---------------------------------------- Local Function ------------------------------------------------------//
      async saveCourseFaq(payload) {
        let result;
        if (payload.id) {
          result = await this.updateCourseFaq(payload);
        } else {
          result = await this.addCourseFaq(payload);
        }
        if (result) {
          this.onCancelClick();
        }
      },
      async initData() {
        const payload = {
          courseOutlineId: this.$route.params.courseOutlineId,
        };
        this.courseOutlineUnits = await this.getCourseOutlineUnitsByCourseOutlineId(payload);
        if (this.courseOutlineUnits && this.courseOutlineUnits.length > 0) {
          this.selectCourseOutlineUnit = this.courseOutlineUnits.find(item => item.id === this.$route.params.courseOutlineUnitId);
        }
      },
      async loadData() {
        if (this.$route.params.id) {
          const payload = {
            id: this.$route.params.id
          };
          this.courseFaq = await this.getCourseFaq(payload);
        }
      },
      clearData() {
        this.courseFaq = {
          question: '',
          answer: '',
        }
      },
    },
    async beforeMount() {
      this.clearData();
      await this.initData();
      await this.loadData();
    }
  }
</script>

<style scoped>

</style>
